// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTask } from '../api/tasks';
import type { Task } from '../types';
import { ProviderAccessWizard } from '@loa/provider-form';
import type { ProviderAccessPageContext, ProviderFormSubmissionRequest, ProviderRejectionRequest } from '@loa/provider-form';
// TODO: Auto-fill wizard fields from task metadata via wizard storage pre-population

export function ProviderFormPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) return;
    getTask(taskId)
      .then(setTask)
      .catch(() => setError('Failed to load task.'))
      .finally(() => setLoading(false));
  }, [taskId]);

  const wizardContext = useMemo<ProviderAccessPageContext | null>(() => {
    if (!task?.metadata) return null;
    const meta = task.metadata;
    const sig = meta.signatories?.[0];
    return {
      brokerageName: meta.brokerage?.name ?? task.brokerageName ?? 'Unknown Brokerage',
      brokerageLogoUrl: meta.brokerage?.logoUrl ?? null,
      policyTypeName: meta.policy?.policyTypeName ?? task.policyTypeName ?? null,
      policyNumberLast4: meta.policy?.policyNumber?.slice(-4) ?? null,
      policyTypeCategory: meta.policy?.policyTypeCategory ?? null,
      providerName: task.providerCode ?? '',
      clientName: sig ? [sig.firstName, sig.lastName].filter(Boolean).join(' ') : null,
      advisorName: meta.advisor ? [meta.advisor.firstName, meta.advisor.lastName].filter(Boolean).join(' ') : null,
      submissionAllowed: true,
      remainingSubmissions: 1,
      tokenExpiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      loaRejected: false,
      lastSubmittedAt: null,
      rejectionReasons: [],
    };
  }, [task]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !task || !wizardContext) {
    return (
      <div className="flex flex-col gap-4">
        <Link to={`/tasks/${taskId}`} className="text-sm text-purple-600 hover:underline">
          Back to task
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Task not found.'}
        </div>
      </div>
    );
  }

  const handleSubmitForm = async (_data: ProviderFormSubmissionRequest): Promise<boolean> => {
    // TODO: submit form data to Provider Server API
    console.log('Form submitted:', _data);
    navigate(`/tasks/${taskId}`);
    return true;
  };

  const handleUploadDocument = async (_file: File): Promise<boolean> => {
    // TODO: upload document to Provider Server API
    console.log('Document uploaded:', _file.name);
    return true;
  };

  const handleRejectLoa = async (_request: ProviderRejectionRequest): Promise<boolean> => {
    // TODO: reject LOA via Provider Server API
    console.log('LOA rejected:', _request);
    navigate(`/tasks/${taskId}`);
    return true;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 mb-2">
        <Link to={`/tasks/${taskId}`} className="text-sm text-purple-600 hover:underline">
          Task {task.policyReferenceCode}
        </Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-600">Enter Details</span>
      </div>

      <ProviderAccessWizard
        token={task.id}
        context={wizardContext}
        submitting={false}
        formOpenDuration={0}
        onSubmitForm={handleSubmitForm}
        onUploadDocument={handleUploadDocument}
        onRejectLoa={handleRejectLoa}
      />
    </div>
  );
}
